import { useEffect, useRef } from 'react';
import { useRouter } from 'react-resource-router';

import { sendPageViewOnRouteChange } from 'services/analytics';

interface UsePageViewTrackingProps {
  userId?: string;
}

/**
 * Fires the page-view lifecycle pair (`requested_page` operational +
 * `viewed page` screen) on every client-side (SPA) route change.
 *
 * Why: the entry page view is emitted once during analytics init
 * (`useInitializeAnalyticsClient` -> `initWithPageAnalytics`). On this SPA, in-app
 * navigations do not re-fire it, so every surface after the first is undercounted
 * in visitor rollups. This hook closes that gap for all community routes.
 *
 * The entry page (first location seen) is intentionally skipped here to avoid
 * double-counting the page that init already reported. Identity/consent behaviour
 * is delegated to the library so counts reconcile with existing visitor queries.
 *
 * Must be rendered inside the react-resource-router context.
 */
export const usePageViewTracking = ({ userId }: UsePageViewTrackingProps): void => {
  const [routerState] = useRouter();
  const currentPath = routerState.location.pathname;

  const isEntryPage = useRef(true);
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    if (isEntryPage.current) {
      isEntryPage.current = false;
      lastTrackedPath.current = currentPath;

      return;
    }

    if (lastTrackedPath.current === currentPath) {
      return;
    }

    lastTrackedPath.current = currentPath;

    void sendPageViewOnRouteChange({ path: currentPath }, userId);
  }, [currentPath, userId]);
};
